/**
 * Production-grade Office Hours Service
 *
 * This service provides a unified, robust interface for managing office hours
 * with lobby closure breaks across create and edit operations.
 *
 * Key Features:
 * - Unified processing for create and edit operations
 * - Comprehensive validation
 * - Robust error handling
 * - Type safety
 * - Idempotent operations
 */

import { OfficeHour, OfficeHoursInput, OfficeHourUpdate } from '../../types/practices/types';
import { ProcessedOfficeHours, processOfficeHoursForAPI, validateOfficeHours } from '../../utils/officeHoursProcessing';
import { ValidationError } from '../../utils/officeHoursValidation';
import { createOfficeHours } from '../practices/createOfficeHours';
import { deleteOfficeHoursBulk } from '../practices/deleteOfficeHours';

/**
 * Helper function to check if day input has empty time fields
 */
const hasEmptyDayInputTimes = (dayInput: {
  open?: string;
  close?: string;
}): boolean => {
  return (
    !dayInput.open ||
    !dayInput.close ||
    dayInput.open.trim() === '' ||
    dayInput.close.trim() === ''
  );
};

/**
 * Helper function to check if office hours have changed
 */
export const hasOfficeHoursChanged = (
  config: OfficeHoursConfig,
  existingOfficeHours: OfficeHour[]
): boolean => {
  // If no existing hours, there are changes
  if (existingOfficeHours.length === 0) {
    return true;
  }

  // Group existing hours by day
  const existingByDay = new Map<number, OfficeHour[]>();
  existingOfficeHours.forEach((hour) => {
    const dayHours = existingByDay.get(hour.day_of_week) || [];
    dayHours.push(hour);
    existingByDay.set(hour.day_of_week, dayHours);
  });

  // Compare each day
  for (
    let dayIndex = 0;
    dayIndex < config.officeHoursInput.length;
    dayIndex++
  ) {
    const dayInput = config.officeHoursInput[dayIndex];
    // Map day name to day of week number (Sunday=1, Monday=2, etc.)
    const dayOfWeek = dayIndex + 1;
    const existingHours = existingByDay.get(dayOfWeek) || [];

    // Check if day should be closed
    const shouldBeClosed =
      dayInput.closed || hasEmptyDayInputTimes(dayInput);

    if (shouldBeClosed) {
      // If day should be closed but has existing hours, there's a change
      if (existingHours.length > 0) {
        return true;
      }
    } else {
      // Day should be open - check if lobby closure is applied
      const shouldApplyLunch =
        config.observesLunchHours &&
        config.lunchHours.start &&
        config.lunchHours.end &&
        dayInput.open < config.lunchHours.start &&
        config.lunchHours.start < config.lunchHours.end &&
        config.lunchHours.end < dayInput.close;

      if (shouldApplyLunch) {
        // Should have 2 segments
        const morningSegment = existingHours.find(
          (h) => !h.closed && !h.end_of_day
        );
        const afternoonSegment = existingHours.find(
          (h) => !h.closed && h.end_of_day
        );

        if (!morningSegment || !afternoonSegment) {
          return true; // Missing segments
        }

        // Check if times match
        if (
          morningSegment.open_time !== dayInput.open ||
          morningSegment.close_time !== config.lunchHours.start ||
          afternoonSegment.open_time !== config.lunchHours.end ||
          afternoonSegment.close_time !== dayInput.close
        ) {
          return true;
        }
      } else {
        // Should have 1 segment
        const singleSegment = existingHours.find((h) => !h.closed);

        if (!singleSegment) {
          return true; // Missing segment
        }

        // Check if times match (normalize for comparison)
        const normalizedOpen =
          dayInput.open?.replace(/[:\s]/g, '') || '';
        const normalizedClose =
          dayInput.close?.replace(/[:\s]/g, '') || '';
        const existingOpen =
          singleSegment.open_time?.replace(/[:\s]/g, '') || '';
        const existingClose =
          singleSegment.close_time?.replace(/[:\s]/g, '') || '';

        if (
          existingOpen !== normalizedOpen ||
          existingClose !== normalizedClose
        ) {
          return true;
        }
      }
    }
  }

  return false; // No changes detected
};

export interface OfficeHoursConfig {
  officeHoursInput: OfficeHoursInput[];
  observesLunchHours: boolean;
  lunchHours: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  };
}

export interface OfficeHoursResult {
  success: boolean;
  data?: {
    created: ProcessedOfficeHours[];
    updated: OfficeHourUpdate[];
    deleted: string[];
  };
  errors: ValidationError[];
  warnings: ValidationError[];
}

export interface OfficeHoursValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * Validates office hours configuration
 */
export const validateOfficeHoursConfig = (
  config: OfficeHoursConfig
): OfficeHoursValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Validate lobby closure hours if enabled
  if (config.observesLunchHours) {
    if (!config.lunchHours.start || !config.lunchHours.end) {
      errors.push({
        field: 'lunchHours',
        message:
          'Lobby closure start and end times are required when lobby closure hours are enabled',
        severity: 'error',
      });
    } else {
      // Validate lobby closure hours format and logic
      const lunchStart = config.lunchHours.start;
      const lunchEnd = config.lunchHours.end;

      if (lunchStart >= lunchEnd) {
        errors.push({
          field: 'lunchHours',
          message:
            'Lobby closure start time must be before lobby closure end time',
          severity: 'error',
        });
      }
    }
  }

  // Validate office hours using existing validation
  const officeHoursErrors = validateOfficeHours(
    config.officeHoursInput,
    config.observesLunchHours ? config.lunchHours : null
  );

  // Convert string errors to ValidationError objects
  officeHoursErrors.forEach((error) => {
    errors.push({
      field: 'officeHours',
      message: error,
      severity: 'error',
    });
  });

  // Check for lobby closure compatibility
  if (
    config.observesLunchHours &&
    config.lunchHours.start &&
    config.lunchHours.end
  ) {
    const lunchStart = config.lunchHours.start;
    const lunchEnd = config.lunchHours.end;

    const incompatibleDays = config.officeHoursInput.filter((day) => {
      if (day.closed || !day.open || !day.close) return false;
      return !(day.open < lunchStart && lunchEnd < day.close);
    });

    if (incompatibleDays.length > 0) {
      warnings.push({
        field: 'lunchHours',
        message:
          `Lobby closure hours will not be applied to: ${incompatibleDays
            .map((d) => d.day)
            .join(', ')}. ` +
          'These days do not span the full lobby closure period.',
        severity: 'warning',
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Processes office hours for API submission
 */
export const processOfficeHoursForSubmission = (
  config: OfficeHoursConfig
): ProcessedOfficeHours[] => {
  const lunchHoursForProcessing =
    config.observesLunchHours &&
    config.lunchHours.start &&
    config.lunchHours.end
      ? { start: config.lunchHours.start, end: config.lunchHours.end }
      : null;

  return processOfficeHoursForAPI(
    config.officeHoursInput,
    lunchHoursForProcessing
  );
};

/**
 * Creates office hours for a new practice
 */
export const createPracticeOfficeHours = async (
  practiceId: string,
  config: OfficeHoursConfig
): Promise<OfficeHoursResult> => {
  try {
    // Validate configuration
    const validation = validateOfficeHoursConfig(config);
    if (!validation.isValid) {
      return {
        success: false,
        errors: validation.errors,
        warnings: validation.warnings,
      };
    }

    // Process office hours
    const processedHours = processOfficeHoursForSubmission(config);

    if (processedHours.length === 0) {
      return {
        success: true,
        data: { created: [], updated: [], deleted: [] },
        errors: [],
        warnings: validation.warnings,
      };
    }

    // Create office hours
    await createOfficeHours(practiceId, processedHours);

    return {
      success: true,
      data: { created: processedHours, updated: [], deleted: [] },
      errors: [],
      warnings: validation.warnings,
    };
  } catch (error) {
    console.error('Error creating office hours:', error);
    return {
      success: false,
      errors: [
        {
          field: 'officeHours',
          message: `Failed to create office hours: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          severity: 'error',
        },
      ],
      warnings: [],
    };
  }
};

/**
 * Updates office hours for an existing practice
 * Uses delete-then-create pattern for clean updates
 */
export const updatePracticeOfficeHours = async (
  practiceId: string,
  config: OfficeHoursConfig,
  existingOfficeHours: OfficeHour[]
): Promise<OfficeHoursResult> => {
  try {
    // Validate configuration
    const validation = validateOfficeHoursConfig(config);
    if (!validation.isValid) {
      return {
        success: false,
        errors: validation.errors,
        warnings: validation.warnings,
      };
    }

  // Check if there are any changes
  const hasChanges = hasOfficeHoursChanged(
    config,
    existingOfficeHours
  );

  if (!hasChanges) {
    // No changes needed, return success
    return {
      success: true,
      data: { created: [], updated: [], deleted: [] },
      errors: [],
      warnings: validation.warnings,
    };
  }

  // Process new office hours
  const processedHours = processOfficeHoursForSubmission(config);

  // Delete all existing office hours first
  const existingIds = existingOfficeHours
    .map((hour) => hour.id)
    .filter((id): id is string => Boolean(id));

  if (existingIds.length > 0) {
    await deleteOfficeHoursBulk(existingIds);
  }

  // Create new office hours
  let createdHours: ProcessedOfficeHours[] = [];
  if (processedHours.length > 0) {
    await createOfficeHours(practiceId, processedHours);
    createdHours = processedHours;
  }

    return {
      success: true,
      data: {
        created: createdHours,
        updated: [],
        deleted: existingIds,
      },
      errors: [],
      warnings: validation.warnings,
    };
  } catch (error) {
    console.error('Error updating office hours:', error);
    return {
      success: false,
      errors: [
        {
          field: 'officeHours',
          message: `Failed to update office hours: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`,
          severity: 'error',
        },
      ],
      warnings: [],
    };
  }
};


/**
 * Unified office hours management function
 */
export const managePracticeOfficeHours = async (
  practiceId: string,
  config: OfficeHoursConfig,
  existingOfficeHours: OfficeHour[] = [],
  isEditMode: boolean = false
): Promise<OfficeHoursResult> => {
  if (isEditMode) {
    return updatePracticeOfficeHours(practiceId, config, existingOfficeHours);
  } else {
    return createPracticeOfficeHours(practiceId, config);
  }
};
